import React, { useMemo } from "react";
import { BPMTable, SuperTopItemType } from "../../Components";
import { dateToMddyy } from "../../utils/date-utils";
import {
  ForecastChartViewLabels,
  ForecastData,
  ForecastKpiViewOption,
  formatCpm,
  formatSpend,
  FullForecastSelection,
  KPI_CI_VIEW_SUFFICES,
  KPI_VIEW_SUFFICES
} from "../forecastingConstants";
import { getKpiFormatter } from "../forecastingUtils";

interface ForecastResultsTableProps {
  data: ForecastData<string>[];
  forecast: FullForecastSelection;
}

const ForecastResultsTable: React.FC<ForecastResultsTableProps> = ({ data, forecast }) => {
  const [tableHeaders, tableSuperHeaders] = useMemo(() => {
    const { outcomeVar, kpiName, primaryBudget } = forecast;
    const totalLabel = ForecastChartViewLabels[outcomeVar][ForecastKpiViewOption.TOTAL];
    const efficiencyLabel = ForecastChartViewLabels[outcomeVar][ForecastKpiViewOption.EFFICIENCY];
    const formatTotalKpi = getKpiFormatter(outcomeVar, ForecastKpiViewOption.TOTAL);
    const formatEfficiencyKpi = getKpiFormatter(outcomeVar, ForecastKpiViewOption.EFFICIENCY);

    const baseHeaderProps = {
      flex: true,
    };

    const headers = [
      {
        ...baseHeaderProps,
        name: "date",
        label: "Date",
        renderer: (row: ForecastData<string>): string => dateToMddyy(row.date),
      },
      {
        ...baseHeaderProps,
        name: "spend",
        label: "Spend",
        renderer: (row: ForecastData<string>): string => formatSpend(row[`${primaryBudget}-spend`]),
      },
      {
        ...baseHeaderProps,
        name: "cpm",
        label: "CPM",
        renderer: (row: ForecastData<string>): string => formatCpm(row[`${primaryBudget}-cpm`]),
      },
      {
        ...baseHeaderProps,
        name: "forecast",
        label: `${kpiName} Estimate`,
        renderer: (row: ForecastData<string>): string =>
          formatTotalKpi(row[`${primaryBudget}-${KPI_VIEW_SUFFICES.Total}`]),
      },
      {
        ...baseHeaderProps,
        name: "forecastCILower",
        label: `${kpiName} Lower CI`,
        renderer: (row: ForecastData<string>): string => {
          const ci = row[`${primaryBudget}-${KPI_CI_VIEW_SUFFICES.Total}`];
          return ci ? formatTotalKpi(ci[0]) : "";
        },
      },
      {
        ...baseHeaderProps,
        name: "forecastCIUpper",
        label: `${kpiName} Upper CI`,
        renderer: (row: ForecastData<string>): string => {
          const ci = row[`${primaryBudget}-${KPI_CI_VIEW_SUFFICES.Total}`];
          return ci ? formatTotalKpi(ci[1]) : "";
        },
      },
      {
        ...baseHeaderProps,
        name: "efficiency",
        label: `${efficiencyLabel} Estimate`,
        renderer: (row: ForecastData<string>): string =>
          formatEfficiencyKpi(row[`${primaryBudget}-${KPI_VIEW_SUFFICES.Efficiency}`]),
      },
      {
        ...baseHeaderProps,
        name: "efficiencyCILower",
        label: `${efficiencyLabel} Lower CI`,
        renderer: (row: ForecastData<string>): string => {
          const ci = row[`${primaryBudget}-${KPI_CI_VIEW_SUFFICES.Efficiency}`];
          return ci ? formatEfficiencyKpi(ci[0]) : "";
        },
      },
      {
        ...baseHeaderProps,
        name: "efficiencyCIUpper",
        label: `${efficiencyLabel} Upper CI`,
        renderer: (row: ForecastData<string>): string => {
          const ci = row[`${primaryBudget}-${KPI_CI_VIEW_SUFFICES.Efficiency}`];
          return ci ? formatEfficiencyKpi(ci[1]) : "";
        },
      },
    ];

    const superHeaders: SuperTopItemType<string>[] = [
      { span: 3, data: "" },
      { span: 3, data: totalLabel },
      { span: 3, data: efficiencyLabel },
    ];

    return [headers, superHeaders];
  }, [forecast]);

  return (
    <div className="forecastResultsTableContainer">
      <BPMTable
        data={data}
        headers={tableHeaders}
        superHeaders={tableSuperHeaders}
        filterBar={false}
      />
    </div>
  );
};

export default ForecastResultsTable;
